import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import Button from "../../components/Button";
import Hero from "../../components/Hero";
import CtaSection from "../../components/CtaSection";
import { globalContent } from "../../config/content";
import styles from "./AssetInformation.module.scss";

const AssetInformation: FC = () => {
  return (
    <div className={styles.assetInfoPage}>
      {/* 1. Hero */}
      <Hero
        eyebrow="Asset Information"
        heading="Connect your assets to the information behind them."
        copy="Buildings contain thousands of pieces of equipment and infrastructure. Pulse gives each asset a structured place within the wider building record, connecting the asset with the documents, drawings and technical information people need."
        backgroundImage={require("../../assets/images/asset-information/asset-information-hero.webp")}
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />

      {/* 2. Problem */}
      <Section bgColor="off-white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>The Challenge</span>
            <h2 className={styles.sectionHeading}>
              Knowing what equipment you have is only the beginning.
            </h2>
            <p className={styles.sectionCopy}>
              A building may contain thousands of assets—from air handling units
              and pumps to chillers, boilers and electrical distribution boards.
              But an asset reference on its own doesn't tell someone everything
              they need to know.
            </p>
            <p className={styles.sectionCopy}>
              The useful information is often spread across equipment manuals,
              datasheets, drawings, specifications, maintenance records,
              warranties and certificates. Pulse brings these relationships
              together.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/asset-information-challenge.webp")}
              alt="Fragmented asset information sources"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 3. More Than an Asset List */}
      <Section bgColor="white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/pulse-asset-screenshot.webp")}
              alt="Pulse asset information record"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Beyond Registers</span>
            <h2 className={styles.sectionHeading}>
              More than a list of equipment.
            </h2>
            <p className={styles.sectionCopy}>
              A traditional asset register might tell you the asset name, level,
              and manufacturer. Pulse provides a much richer digital record,
              turning each asset into a starting point for accessing the
              information associated with it.
            </p>
            <div className={styles.actionLink}>
              <Link
                to="/digital-building-information"
                className={styles.textLink}
              >
                Explore Digital Building Information →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. The Asset Record */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Asset Records</span>
          <h2 className={styles.sectionHeading}>
            Give every important asset a digital record.
          </h2>
          <p className={styles.sectionCopy}>
            Pulse provides a structured record for each asset, helping users
            understand what the asset is, where it is located, and what
            information is connected to it.
          </p>
        </div>
        <div className={styles.assetGridList}>
          <div>Asset Type: Air Handling Unit</div>
          <div>Location: Level 03 - Mechanical Plant</div>
          <div>Manufacturer & Model Records</div>
          <div>Technical Specifications & Datasheets</div>
          <div>Associated O&M Manuals</div>
          <div>Associated CAD & As-Built Drawings</div>
          <div>Maintenance Procedures</div>
          <div>Warranty & Certification Data</div>
        </div>
      </Section>

      {/* 5. Connect Assets to Their Information */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Connected Data</span>
            <h2 className={styles.sectionHeading}>
              The asset is the starting point.
            </h2>
            <p className={styles.sectionCopy}>
              The real value comes from connecting the asset directly to the
              information behind it. Users can move seamlessly from an asset
              record to manuals, datasheets, drawings, maintenance instructions
              and warranties without searching through unrelated folders.
            </p>
            <div className={styles.actionLink}>
              <Link to="/documents-drawings" className={styles.textLink}>
                Explore Documents & Drawings →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/asset-information-relationships.webp")}
              alt="Asset relationship diagram"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 6. Documents & Drawings */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/asset-information-documents.webp")}
              alt="Asset and technical document relationships"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Technical Records</span>
            <h2 className={styles.sectionHeading}>
              Find the documents that belong to an asset.
            </h2>
            <p className={styles.sectionCopy}>
              An asset rarely exists in isolation. Pulse connects assets with
              relevant technical documentation, schematics and drawings.
            </p>
            <table className={styles.assetTable}>
              <thead>
                <tr>
                  <th>Information Type</th>
                  <th>Connected Record</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Asset</td>
                  <td>AHU-003 (Air Handling Unit)</td>
                </tr>
                <tr>
                  <td>Manual</td>
                  <td>AHU-003 O&M Manual & Datasheet</td>
                </tr>
                <tr>
                  <td>Drawing</td>
                  <td>Mechanical Schematic M-402</td>
                </tr>
                <tr>
                  <td>Controls</td>
                  <td>AHU Controls & BMS Integration</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* 7. Manufacturer Information */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Manufacturer Records</span>
            <h2 className={styles.sectionHeading}>
              Keep manufacturer information with the asset.
            </h2>
            <p className={styles.sectionCopy}>
              Manufacturer details, product datasheets, contact info and
              original equipment specifications remain accessible right
              alongside the asset record throughout its operational lifespan.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/asset-information-manufacturer.webp")}
              alt="Manufacturer information associated with a building asset"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 8. Maintenance & Technical Information */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/asset-information-maintenance.webp")}
              alt="Engineer accessing building maintenance information"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Operations & Maintenance</span>
            <h2 className={styles.sectionHeading}>
              Keep operating and maintenance info close at hand.
            </h2>
            <p className={styles.sectionCopy}>
              Give engineers and facilities teams instant access to maintenance
              schedules, service procedures, replacement parts information and
              operational requirements when they need them most.
            </p>
            <div className={styles.actionLink}>
              <Link to="/digital-om" className={styles.textLink}>
                Explore Digital O&M →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 9. Asset Relationships */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Building Context</span>
            <h2 className={styles.sectionHeading}>
              Assets don't exist in isolation.
            </h2>
            <p className={styles.sectionCopy}>
              Buildings are made of connected systems. Assets relate to floors,
              plant rooms, control loops and drawings, building a richer digital
              representation of how the building's systems fit together.
            </p>
            <div className={styles.actionLink}>
              <Link
                to="/digital-building-information"
                className={styles.textLink}
              >
                Explore Digital Building Information →
              </Link>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/pulse-room-screenshot.webp")}
              alt="Pulse room information and associated building assets"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 10. Find Assets Quickly */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/search/search-problem.webp")}
              alt="Pulse search interface for assets"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Fast Search</span>
            <h2 className={styles.sectionHeading}>
              Find the asset without knowing where information lives.
            </h2>
            <p className={styles.sectionCopy}>
              Search by asset tag, reference number, location, equipment type or
              manufacturer to jump directly to the asset record and its
              connected documents.
            </p>
            <div className={styles.actionLink}>
              <Link to="/search" className={styles.textLink}>
                Explore Search →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 11. Access Assets on Site & 12. QR Codes */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>On-Site Access & QR Codes</span>
            <h2 className={styles.sectionHeading}>
              Go from the physical asset straight to its information.
            </h2>
            <p className={styles.sectionCopy}>
              Scan a QR code attached to physical equipment in a plant room to
              go directly to that asset's Pulse record on your mobile device.
            </p>
            <p className={styles.sectionCopy}>
              Access O&M manuals, drawings and service history instantly where
              the work actually happens.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/assets/asset-qr-code.webp")}
              alt="Asset QR code scanning"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 13. AI & Asset Information */}
      <Section bgColor="off-white">
        <div className={styles.splitLayoutReverse}>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/asset-information-ai.webp")}
              alt="AI-assisted building asset information"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Pulse AI</span>
            <h2 className={styles.sectionHeading}>
              Ask questions about your assets.
            </h2>
            <p className={styles.sectionCopy}>
              Use Pulse AI to ask natural questions about equipment—such as
              manufacturer details, location, maintenance history and associated
              drawings—with answers grounded in your actual building records.
            </p>
            <div className={styles.actionLink}>
              <Link to="/ai" className={styles.textLink}>
                Explore AI →
              </Link>
            </div>
          </div>
        </div>
      </Section>

      {/* 14. Lifecycle */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Building Lifecycle</span>
            <h2 className={styles.sectionHeading}>
              Asset information shouldn't stop at handover.
            </h2>
            <p className={styles.sectionCopy}>
              Pulse provides a continuous digital environment where asset data
              and documentation remain accessible through design, construction,
              handover, operation and ongoing building management.
            </p>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/asset-information-lifecycle.webp")}
              alt="Asset information building lifecycle"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 15. Benefits */}
      <Section bgColor="off-white">
        <div className={styles.sectionHeader}>
          <span className={styles.eyebrow}>Benefits</span>
          <h2 className={styles.sectionHeading}>
            Make asset information more useful.
          </h2>
        </div>
        <div className={styles.benefitGrid}>
          <div className={styles.benefitCard}>
            <h3>Find information faster</h3>
            <p>
              Move directly from an asset reference to the records behind it.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Give assets context</h3>
            <p>
              Connect equipment with documents, drawings and wider building
              systems.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Improve access on site</h3>
            <p>
              Give teams instant access to technical data wherever they are
              working.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Preserve knowledge</h3>
            <p>
              Keep vital technical records accessible beyond individual staff
              members.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Reduce hunting</h3>
            <p>
              Spend less time searching through fragmented folders and PDFs.
            </p>
          </div>
          <div className={styles.benefitCard}>
            <h3>Build smarter tools</h3>
            <p>
              Structured asset data powers search, AI and future building
              capabilities.
            </p>
          </div>
        </div>
      </Section>

      {/* 16. Product Showcase */}
      <Section bgColor="white">
        <div className={styles.splitLayout}>
          <div className={styles.splitContent}>
            <span className={styles.eyebrow}>Product Showcase</span>
            <h2 className={styles.sectionHeading}>
              See asset information in Pulse.
            </h2>
            <p className={styles.sectionCopy}>
              Experience how buildings, assets, documents, drawings, search and
              AI come together in a unified platform.
            </p>
            <div className={styles.actionLink}>
              <Button to={globalContent.demoUrl} variant="primary" size="md">
                Explore the Demo →
              </Button>
            </div>
          </div>
          <div className={styles.splitImageContainer}>
            <img
              src={require("../../assets/images/asset-information/asset-information-showcase.webp")}
              alt="Building asset information and connected technical records"
              className={styles.productScreenshot}
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* 17. Final CTA */}
      <CtaSection
        eyebrow="Get Started"
        heading="Make your building assets easier to understand and use."
        copy="Whether you have an existing asset register, O&M manuals or thousands of technical documents, Pulse provides a structured digital environment for connecting your building information."
        button={{
          text: "Explore the Demo",
          to: globalContent.demoUrl,
          variant: "secondary",
        }}
        secondaryButton={{
          text: "Request a Demo",
          to: "/contact",
          variant: "outlineLight",
        }}
      />
    </div>
  );
};

export default withPage(AssetInformation)({
  meta: globalContent.seo.assetInformation,
});
